import { useSyncExternalStore } from 'react';
import { flushOutbox } from './submit';
import { onOutboxChange, listOutbox } from '../db/outbox';

/**
 * Single background sync driver for the whole app (not one per component).
 * Background Sync API is unavailable on iOS Safari and unreliable for our
 * redirecting cross-origin POST, so we retry in the foreground: on reconnect,
 * on tab focus, and on a slow timer — and ONLY when the outbox is non-empty.
 */
const POLL_MS = 3 * 60 * 1000;

let started = false;
let busy = false;
let pending = 0;
let rejected = 0;
const subs = new Set<() => void>();
const emit = () => subs.forEach((f) => f());

async function refreshPending() {
  try {
    const all = await listOutbox();
    const n = all.filter((i) => i.status === 'pending' || i.status === 'syncing').length;
    const r = all.filter((i) => i.status === 'rejected').length;
    if (n !== pending || r !== rejected) { pending = n; rejected = r; emit(); }
  } catch { /* ignore */ }
}

async function run() {
  if (busy || navigator.onLine === false) return;
  await refreshPending();
  if (pending === 0) return; // nothing queued -> no network call
  busy = true; emit();
  try {
    await flushOutbox();
  } finally {
    busy = false;
    await refreshPending();
    emit();
  }
}

export function startSyncController() {
  if (started) return;
  started = true;
  // a new item (e.g. an optimistic reading) should flush right away, not wait for the poll
  onOutboxChange(() => void run());
  window.addEventListener('online', () => void run());
  document.addEventListener('visibilitychange', () => {
    if (document.visibilityState === 'visible') void run();
  });
  window.setInterval(() => void run(), POLL_MS);
  void refreshPending();
  void run();
}

const snapshot = () => ({ pending, busy, rejected });
let cached = snapshot();
function getSnapshot() {
  const s = snapshot();
  if (s.pending !== cached.pending || s.busy !== cached.busy || s.rejected !== cached.rejected) cached = s;
  return cached;
}

export function useSync() {
  const state = useSyncExternalStore(
    (cb) => { subs.add(cb); return () => subs.delete(cb); },
    getSnapshot,
    getSnapshot,
  );
  return { ...state, sync: run };
}
